import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, Home, AlertTriangle } from 'lucide-react';
import './AccountSuspended.css';

function AccountSuspended() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { suspendedUntil, reason } = location.state || {};
  
  const isRTL = i18n.language === 'ar';
  
  // Format suspension end date
  const formatSuspensionDate = () => {
    if (!suspendedUntil) return '';
    try {
      const date = new Date(suspendedUntil);
      return date.toLocaleDateString(t('accoreco.enus'), {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return suspendedUntil;
    }
  };
  
  // Calculate remaining time
  const getRemainingTime = () => {
    if (!suspendedUntil) return '';
    try {
      const endDate = new Date(suspendedUntil);
      const now = new Date();
      const diff = endDate - now;
      
      if (diff <= 0) return t('accosusp.suspensionEnded');
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      
      if (days > 0) {
        return isRTL 
          ? `${days} يوم و ${hours} ساعة متبقية`
          : `${days} days and ${hours} hours remaining`;
      }
      return isRTL 
        ? `${hours} ساعة متبقية`
        : `${hours} hours remaining`;
    } catch {
      return '';
    }
  };
  
  return (
    <div className="suspended-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="suspended-container">
        <div className="suspended-card">
          {/* Header */}
          <div className="suspended-header">
            <div className="suspended-icon">
              <Clock size={48} />
            </div>
            <h1 className="suspended-title" data-testid="suspended-title">
              {t('accountSuspendedTitle')}
            </h1>
            <p className="suspended-subtitle">
              {t('accountSuspendedSubtitle')}
            </p>
          </div>
          
          {/* Info Box */}
          <div className="suspension-info">
            {/* Reason */}
            {reason && (
              <div className="info-row">
                <AlertTriangle size={18} />
                <div className="info-content">
                  <span className="info-label">{t('suspensionReason')}:</span>
                  <span className="info-value">{reason}</span>
                </div>
              </div>
            )}
            
            {/* End Date */}
            <div className="info-row">
              <Clock size={18} />
              <div className="info-content">
                <span className="info-label">{t('suspensionEnds')}:</span>
                <span className="info-value">{formatSuspensionDate()}</span>
              </div>
            </div>
            
            {/* Remaining Time */}
            <div className="remaining-time" data-testid="remaining-time">
              {getRemainingTime()}
            </div>
          </div>
          
          {/* What you can do */}
          <div className="what-to-do">
            <h3>{t('accosusp.whatCanYouDo')}</h3>
            <ul>
              <li>{t('accosusp.waitUntilTheSuspensionPeriod')}</li>
              <li>{t('accosusp.reviewOurPoliciesToAvoid')}</li>
              <li>{t('accosusp.afterTheSuspensionEndsYou')}</li>
            </ul>
          </div>
          
          {/* Footer */}
          <div className="suspended-footer">
            <button
              onClick={() => navigate('/')}
              className="home-btn"
              data-testid="go-home-btn"
            >
              <Home size={18} />
              <span>{t('goToHome')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountSuspended;
